import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiService } from '../../services/api.service';
import { DropdownOption } from '../components/simple-dropdown/simple-dropdown.component';

export const ALL_LANES_FILTER_OPTION: DropdownOption = {
  label: 'All Lanes',
  value: '',
};

interface LaneListItem {
  id?: string;
  name?: string;
  status?: string;
}

/** Build "All Lanes" + active lane options for table filters. */
export function buildLaneFilterOptions(lanes: LaneListItem[]): DropdownOption[] {
  const active = (lanes || []).filter((lane) => {
    const status = String(lane.status ?? 'active').toLowerCase();
    return status === 'active' && !!String(lane.id || '').trim() && !!String(lane.name || '').trim();
  });
  return [
    ALL_LANES_FILTER_OPTION,
    ...active.map((lane) => ({
      label: String(lane.name).trim(),
      value: String(lane.id).trim(),
    })),
  ];
}

/**
 * Whether the lane filter should be shown. Hide it when there is at most one
 * selectable lane, since "All Lanes" vs a single lane offers no real choice.
 */
export function shouldShowLaneFilter(options: DropdownOption[] | null | undefined): boolean {
  return (options || []).filter((option) => !!String(option.value || '').trim()).length > 1;
}

/** Load active lanes for a location as dropdown options (All Lanes first). */
export function loadLaneFilterOptions(
  apiService: ApiService,
  locationId: string | null | undefined
): Observable<DropdownOption[]> {
  const normalized = locationId != null ? String(locationId).trim() : '';
  if (!normalized) {
    return of([ALL_LANES_FILTER_OPTION]);
  }

  return apiService
    .get<LaneListItem[]>(`location/view-location/${encodeURIComponent(normalized)}/lanes`)
    .pipe(
      map((response) => {
        const lanes =
          response.status && Array.isArray(response.data) ? (response.data as LaneListItem[]) : [];
        return buildLaneFilterOptions(lanes);
      }),
      catchError(() => of([ALL_LANES_FILTER_OPTION]))
    );
}
